import { create } from "zustand";
import { devtools } from "zustand/middleware";
import config from "../config";
import {
  apiResult,
  ContactsDBItem,
  Contact,
  ContactsDBObject,
  Filter,
} from "../interfaces";
import {
  adaptItem,
  howMany,
  runChatList,
  contactAdapter,
} from "../utils/contacts";
import useContactStore from "./contact";

const isDev = config.ENV === "staging" || config.ENV === "local";

interface ContactStoreState {
  contactsDb: ContactsDBItem[];
  contactsDbObject: ContactsDBObject;
  contactList: Contact[];
  searchResults: Contact[] | false;
  isSearching: boolean; // fetching results from backend;
  searchActive: boolean; // search state is active
  currentContact: string;
  loading: boolean;
  escalatedCount: number;
  filters: Filter;

  setLoading: (loading: boolean) => void;
  updateContactsDb: (contacts: apiResult[], isWebChat?: boolean) => void;
  setSearchResults: (contacts: apiResult[]) => void;
  resetSearchResults: () => void;
  setSearchActive: (searchActive: boolean) => void;
  setIsSearching: (isSearching: boolean) => void;
  setFilters: (filters: Filter) => void;
  insertSentMessage: () => void;
  modifyEnabled: () => void;
  changeContactStatus: () => void;
  readMessages: () => void;
}

const useContactsStore = create(
  devtools<ContactStoreState>((set, get) => ({
    contactsDb: [],
    contactsDbObject: {},
    contactList: [],
    searchResults: false,
    currentContact: "",
    loading: true,
    escalatedCount: 0,
    isSearching: false,
    searchActive: false,
    filters: {
      simple: [],
      companies: [],
    },

    setLoading: (loading) => set({ loading }),

    updateContactsDb: (contacts: apiResult[], isWebChat = false) => {
      const currentContactsDbObject = get().contactsDbObject;
      const updatedContactsDbObject = { ...currentContactsDbObject };

      contacts.forEach((c) => {
        const properties = howMany(c.messages, [
          "user",
          "assistant",
          "outbound",
        ]);
        const contact = adaptItem(c, properties);
        // @ts-ignore
        updatedContactsDbObject[contact.contact.id] = contact;
      });

      // Specific update for the current contact or web chat
      const contactStore = useContactStore.getState();
      if (isWebChat) {
        contactStore.setContactId(contacts[0]._id);
        contactStore.setContact(contacts[0]);
      }

      const { list, escalated } = runChatList(updatedContactsDbObject);
      const filteredList = isDev
        ? list
        : list.filter((contact) => contact.type !== "play");

      set({
        contactsDbObject: updatedContactsDbObject,
        contactList: filteredList,
        escalatedCount: escalated,
      });
    },

    setFilters: (filters: Filter) => set({ filters }),
    setSearchResults: (contacts: apiResult[]) => {
      set({
        searchResults: contacts
          .map((c) => contactAdapter(c, {}))
          .sort((a, b) => b.timestampNative - a.timestampNative),
      });
    },
    resetSearchResults: () => set({ searchResults: false }),
    setIsSearching: (isSearching: boolean) => set({ isSearching }),
    setSearchActive: (searchActive: boolean) => set({ searchActive }),
    insertSentMessage: () => {},
    modifyEnabled: () => {},
    changeContactStatus: () => {},
    readMessages: () => {},
  }))
);

export default useContactsStore;
